import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Box,
    Button,
    TextField,
    Typography,
    Paper,
    Alert,
    CircularProgress
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { api } from '../../services/api';
import { ErrorDisplay } from '../../components/common/ErrorDisplay';

const validationSchema = Yup.object({
    password: Yup.string()
        .min(8, 'Password must be at least 8 characters')
        .required('Password is required'),
    confirmPassword: Yup.string()
        .oneOf([Yup.ref('password')], 'Passwords must match')
        .required('Confirm password is required')
});

export const ResetPassword: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    const formik = useFormik({
        initialValues: {
            password: '',
            confirmPassword: ''
        },
        validationSchema,
        onSubmit: async (values) => {
            setLoading(true);
            setError(null);
            try {
                await api.post('/api/v1/auth/reset-password', {
                    token,
                    new_password: values.password
                });
                setSuccess(true);
            } catch (err: any) {
                setError(err.response?.data?.detail || 'An error occurred');
            } finally {
                setLoading(false);
            }
        }
    });

    if (!token) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '100vh',
                    p: 2
                }}
            >
                <Paper
                    elevation={3}
                    sx={{
                        p: 4,
                        maxWidth: 400,
                        width: '100%',
                        textAlign: 'center'
                    }}
                >
                    <Typography variant="h5" component="h1" gutterBottom>
                        Invalid Reset Link
                    </Typography>
                    <Typography variant="body1" color="text.secondary" paragraph>
                        The password reset link is invalid or has expired.
                    </Typography>
                    <Button
                        variant="contained"
                        onClick={() => navigate('/forgot-password')}
                        sx={{ mt: 2 }}
                    >
                        Request New Link
                    </Button>
                </Paper>
            </Box>
        );
    }

    if (success) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '100vh',
                    p: 2
                }}
            >
                <Paper
                    elevation={3}
                    sx={{
                        p: 4,
                        maxWidth: 400,
                        width: '100%',
                        textAlign: 'center'
                    }}
                >
                    <Typography variant="h5" component="h1" gutterBottom>
                        Password Reset Successful
                    </Typography>
                    <Typography variant="body1" color="text.secondary" paragraph>
                        Your password has been reset successfully. You can now log in with your new password.
                    </Typography>
                    <Button
                        variant="contained"
                        onClick={() => navigate('/login')}
                        sx={{ mt: 2 }}
                    >
                        Go to Login
                    </Button>
                </Paper>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100vh',
                p: 2
            }}
        >
            <Paper
                elevation={3}
                sx={{
                    p: 4,
                    maxWidth: 400,
                    width: '100%'
                }}
            >
                <Typography variant="h5" component="h1" gutterBottom align="center">
                    Reset Password
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph align="center">
                    Please enter your new password.
                </Typography>

                {error && (
                    <ErrorDisplay
                        error={error}
                        userMessage={typeof error === 'string' ? error : 'Failed to reset password.'}
                    />
                )}

                <form onSubmit={formik.handleSubmit}>
                    <TextField
                        fullWidth
                        id="password"
                        name="password"
                        label="New Password"
                        type="password"
                        value={formik.values.password}
                        onChange={formik.handleChange}
                        error={formik.touched.password && Boolean(formik.errors.password)}
                        helperText={formik.touched.password && formik.errors.password}
                        margin="normal"
                        disabled={loading}
                    />

                    <TextField
                        fullWidth
                        id="confirmPassword"
                        name="confirmPassword"
                        label="Confirm New Password"
                        type="password"
                        value={formik.values.confirmPassword}
                        onChange={formik.handleChange}
                        error={formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword)}
                        helperText={formik.touched.confirmPassword && formik.errors.confirmPassword}
                        margin="normal"
                        disabled={loading}
                    />

                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        disabled={loading}
                        sx={{ mt: 3, mb: 2 }}
                    >
                        {loading ? <CircularProgress size={24} /> : 'Reset Password'}
                    </Button>

                    <Button
                        fullWidth
                        variant="text"
                        onClick={() => navigate('/login')}
                        disabled={loading}
                    >
                        Back to Login
                    </Button>
                </form>
            </Paper>
        </Box>
    );
}; 